import { useQuery } from '@apollo/client';
import { RuleVariant, RuleVariantUnion } from '@procureai/rule-builder';
import { RuleBuilderProps } from '@procureai/rule-builder-ui-mui';
import { useCustomSnackbar } from '@procureai/ui-mui';
import { indexBy, prop } from 'ramda';
import { useMemo } from 'react';

import { RULE_EVALUATION_LOG_DETAIL_QUERY } from '~/routes/RuleEvaluationLogDetail/RuleEvaluationLogDetailQuery.gql.ts';

interface UseRuleEvaluationLogDetailProps {
  ruleId: string;
  evaluationLogId: string;
}

type UseRuleEvaluationLogDetailResult = {
  stats: RuleBuilderProps['stats'] | undefined;
  variant: RuleVariantUnion;
  data: RuleBuilderProps['data'];
  loading: boolean;
};

export const useRuleEvaluationLogDetail = ({
  ruleId,
  evaluationLogId,
}: UseRuleEvaluationLogDetailProps): UseRuleEvaluationLogDetailResult => {
  const { onError } = useCustomSnackbar();

  const { data, loading } = useQuery(RULE_EVALUATION_LOG_DETAIL_QUERY, {
    variables: {
      ruleId,
      evaluationLogId,
    },
    onError,
    fetchPolicy: 'cache-and-network',
  });

  const ruleVersion = data?.tesseract.rule.evaluationLog.ruleVersion;
  const graph = (ruleVersion?.graph ?? null) as RuleBuilderProps['data'];
  const statsData = ruleVersion?.stats;
  const stats = useMemo<RuleBuilderProps['stats']>(() => {
    if (!statsData || !graph) {
      return undefined;
    }
    const edgesById = indexBy(
      prop('itemId'),
      statsData.edges.map(({ itemId, hits }) => ({ itemId, highlighted: hits > 0 })),
    );
    const nodes: NonNullable<RuleBuilderProps['stats']>['nodes'] = {};

    graph.edges.forEach(({ id, source, target }) => {
      const edge = edgesById[id];
      if (edge?.highlighted) {
        const nodeStats = { highlighted: true };
        nodes[source] = nodeStats;
        nodes[target] = nodeStats;
      }
    });
    return {
      nodes: nodes,
      edges: edgesById,
    };
  }, [statsData, graph]);

  return {
    data: graph,
    stats,
    variant: RuleVariant.SOURCING,
    loading,
  };
};
